import { z } from 'zod';
/**
 * RBAC0 — 역할 정의. 기존 `RoleSchema` (사용자 계정 role 'admin'|'user') 와 구분하기 위해
 * Rbac 접두사를 사용. 역할이 가진 skill 은 별도 grant 테이블로 N:M.
 */
/** 시스템 그룹 종류 — null 이면 일반 사용자 정의 그룹. */
export const RbacRoleSystemKindSchema = z.enum(['all_users']);
export const RbacRoleSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100),
    description: z.string().nullable(),
    enabled: z.boolean(),
    /** 시스템 그룹 표시 — 'all_users' 면 룰/수동 멤버 불가 + 삭제 차단. */
    systemKind: RbacRoleSystemKindSchema.nullable(),
    /** 이 역할이 부여한 skill 의 id 배열. list 응답에도 항상 포함. */
    skillIds: z.array(z.string().uuid()),
    /** 이 역할을 보유한 사용자 수. systemKind='all_users' 면 자동 부여 대상이라 의미 다름. */
    memberCount: z.number().int().nonnegative(),
    /** 이 역할을 보유한 사용자 id 배열. 상세(detail) 응답에서만 채우고 list 에선 빈 배열. */
    memberIds: z.array(z.string().uuid()),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const RbacRoleListResponseSchema = z.object({
    roles: z.array(RbacRoleSchema),
});
export const CreateRbacRoleRequestSchema = z.object({
    name: z.string().min(1).max(100),
    description: z.string().nullable().optional(),
    enabled: z.boolean().optional(),
    /** 생성과 동시에 skill 부여 (선택). */
    skillIds: z.array(z.string().uuid()).optional(),
});
export const UpdateRbacRoleRequestSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().nullable().optional(),
    enabled: z.boolean().optional(),
});
/** 역할의 skill 화이트리스트 전체 교체 (set semantics). */
export const SetRoleSkillsRequestSchema = z.object({
    skillIds: z.array(z.string().uuid()),
});
/** 역할의 멤버 사용자 전체 교체 (set semantics). */
export const SetRoleMembersRequestSchema = z.object({
    userIds: z.array(z.string().uuid()),
});
/** 사용자의 역할 전체 교체 (set semantics) — `/admin/users/:id` 측 진입점. */
export const SetUserRolesRequestSchema = z.object({
    roleIds: z.array(z.string().uuid()),
});
/** effective skill 의 출처 (UI 뱃지용). */
export const EffectiveSkillSourceSchema = z.discriminatedUnion('kind', [
    /** user_skill_grants 직접 부여. */
    z.object({
        kind: z.literal('direct'),
    }),
    /** user_role_assignments → role_skill_grants 경로. */
    z.object({
        kind: z.literal('role'),
        roleId: z.string().uuid(),
        roleName: z.string(),
    }),
    /** role_attribute_grants 룰로 자동 부여된 역할 경로. */
    z.object({
        kind: z.literal('attribute'),
        roleId: z.string().uuid(),
        roleName: z.string(),
        /** 어떤 룰이 매칭됐는지 (UI 표시 + 디버깅). */
        attributeKey: z.string(),
        attributeValue: z.string(),
        matchMode: z.enum(['exact', 'prefix']),
        /** true 면 "이 값이 아닐 때" 매칭 (negate). false 면 일반 매칭. */
        negate: z.boolean(),
    }),
    /** all_users 시스템 역할 (전체 사용자 자동 부여) 경로. */
    z.object({
        kind: z.literal('all_users'),
        roleId: z.string().uuid(),
        roleName: z.string(),
    }),
]);
// ---------------------- Phase 2: role_attribute_grants ----------------------
export const RbacMatchModeSchema = z.enum(['exact', 'prefix']);
export const RbacRoleAttributeGrantSchema = z.object({
    roleId: z.string().uuid(),
    attributeKey: z.string().min(1).max(100),
    attributeValue: z.string().min(1).max(500),
    matchMode: RbacMatchModeSchema,
    /** true 면 "이 값이 아닐 때" 매칭 (NOT IN). false 면 일반 (IN). */
    negate: z.boolean(),
    createdAt: z.string(),
});
/**
 * 역할의 자동 부여 룰 전체 교체 (set semantics).
 *
 * 평가 의미 (matcher 가 동일 attributeKey 끼리 묶어 처리):
 *   - positive (negate=false) 끼리는 OR — 사용자 값이 어느 하나라도 매칭하면 OK
 *   - negate (negate=true) 끼리는 AND NOT — 사용자 값이 단 하나도 매칭하지 않아야 OK
 *   - 다른 attributeKey 끼리는 AND
 * positive 가 0개여도 negate 만으로 동작 (NOT IN 단독 룰 허용).
 */
export const SetRoleAttributeGrantsRequestSchema = z.object({
    rules: z.array(z.object({
        attributeKey: z.string().min(1).max(100),
        attributeValue: z.string().min(1).max(500),
        matchMode: RbacMatchModeSchema.default('prefix'),
        negate: z.boolean().default(false), // 호환: 옛 클라가 안 보내도 false 로 채움
    })),
});
export const RoleAttributeGrantsResponseSchema = z.object({
    roleId: z.string().uuid(),
    rules: z.array(z.object({
        attributeKey: z.string(),
        attributeValue: z.string(),
        matchMode: RbacMatchModeSchema,
        negate: z.boolean(),
    })),
});
// ---------------------- Phase 2: user_role_exclusions ----------------------
export const RbacUserRoleExclusionSchema = z.object({
    userId: z.string().uuid(),
    roleId: z.string().uuid(),
    reason: z.string().nullable(),
    createdAt: z.string(),
});
export const AddRoleExclusionRequestSchema = z.object({
    roleId: z.string().uuid(),
    reason: z.string().max(500).nullable().optional(),
});
/** 사용자별 effective skill 한 항목 — 동일 skill 이 여러 출처를 가질 수 있음. */
export const EffectiveSkillEntrySchema = z.object({
    skillId: z.string().uuid(),
    skillName: z.string(),
    sources: z.array(EffectiveSkillSourceSchema).min(1),
});
//# sourceMappingURL=rbac.js.map